import { ClipboardCheck, Compass, Hammer, Lock, Medal } from 'lucide-react';

export const PLAN_OPTIONS = [
  { value: 'Career Clarity Session (₹499)', name: 'Career Clarity Session', price: '₹499', was: '₹649', duration: '45 mins', tag: 'Start here' },
  { value: 'Career Direction Blueprint (₹2,999)', name: 'Career Direction Blueprint', price: '₹2,999', was: '₹3,499', duration: '~2 weeks' },
  { value: 'Role Skills Execution (₹3,499)', name: 'Role Skills Execution', price: '₹3,499', was: '₹4,499', duration: '8 weeks' },
  { value: 'Career Readiness Program (₹5,999)', name: 'Career Readiness Program', price: '₹5,999', was: '₹7,499', duration: '8 weeks' },
  { value: 'Integrated Career Journey (₹8,500)', name: 'Integrated Career Journey', price: '₹8,500', was: '₹10,999', duration: '8 weeks', tag: 'Best value' },
];

export const inputCls = 'h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-0 transition-colors';

const STAGES = ['Your details', 'Secure payment', 'Kickoff call'];

export const ApplyHero = ({ isSession }) => (
  <section className="bg-[#0b1f4d] text-white relative overflow-hidden" data-testid="apply-hero">
    <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-teal-400/10 blur-3xl" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-16 relative">
      <p className="text-[11px] font-semibold tracking-[0.25em] uppercase mb-3 text-teal-300">{isSession ? 'Book a session' : 'Program enrollment'}</p>
      <h1 className="font-outfit font-bold text-4xl sm:text-5xl leading-tight max-w-2xl">
        {isSession ? 'Book Your' : 'Enroll in Your'}{' '}
        <span className="text-teal-300" data-testid="apply-heading-kind">{isSession ? 'Career Session' : 'Career Program'}</span>
      </h1>
      <p className="mt-4 text-slate-300 max-w-xl">Fill out the form and complete payment to confirm your {isSession ? 'booking' : 'enrollment'}. It takes about 3 minutes.</p>
      <ol className="mt-8 flex flex-wrap gap-3" data-testid="apply-stages">
        {STAGES.map((s, i) => (
          <li key={s} className={`flex items-center gap-2 text-sm rounded-full pl-1.5 pr-4 py-1.5 border ${i === 0 ? 'bg-teal-400 text-[#0b1f4d] border-teal-400 font-semibold' : 'border-white/15 text-slate-300'}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#0b1f4d] text-teal-300' : 'bg-white/10'}`}>{i + 1}</span>{s}
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const Step = ({ n, title, hint, children, testid }) => (
  <fieldset className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7" data-testid={testid}>
    <legend className="sr-only">{title}</legend>
    <div className="flex items-start gap-4 mb-6">
      <span className="w-9 h-9 shrink-0 rounded-xl bg-[#0b1f4d] text-teal-300 font-outfit font-bold flex items-center justify-center">{String(n).padStart(2, '0')}</span>
      <div><p className="font-outfit font-semibold text-lg text-[#0b1f4d]">{title}</p>{hint && <p className="text-sm text-slate-500 mt-0.5">{hint}</p>}</div>
    </div>
    {children}
  </fieldset>
);

export const PlanPicker = ({ value, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" data-testid="interested-plan-select">
    {PLAN_OPTIONS.map((p, i) => {
      const on = value === p.value;
      return (
        <button type="button" role="radio" aria-checked={on} key={p.value} onClick={() => onChange(p.value)} data-testid={`plan-option-${i}`}
          className={`relative text-left rounded-xl border p-4 transition-[border-color,background-color,box-shadow] ${on ? 'border-teal-500 bg-teal-50/70 shadow-[0_0_0_3px_rgba(45,212,191,0.25)]' : 'border-slate-200 hover:border-teal-300 bg-white'} ${i === PLAN_OPTIONS.length - 1 ? 'sm:col-span-2' : ''}`}>
          {p.tag && <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider bg-[#0b1f4d] text-teal-300 px-2 py-0.5 rounded-full">{p.tag}</span>}
          <span className={`inline-flex w-4 h-4 rounded-full border-2 mb-2 ${on ? 'border-teal-600 bg-teal-600 shadow-[inset_0_0_0_2px_white]' : 'border-slate-300'}`} />
          <p className="font-semibold text-[#0b1f4d] text-sm pr-16">{p.name}</p>
          <p className="text-sm text-slate-500 mt-0.5"><s className="text-slate-400 mr-1.5" data-testid={`plan-option-was-${i}`}>{p.was}</s><span className="font-semibold text-[#0b1f4d]">{p.price}</span> · {p.duration}</p>
        </button>
      );
    })}
  </div>
);

export const OrderSummary = ({ name, price, was, duration, benefits }) => (
  <div className="rounded-3xl bg-[#0b1f4d] text-white p-7 shadow-[0_20px_60px_rgba(11,31,77,0.25)]" data-testid="apply-order-summary">
    <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-teal-300">Your selection</p>
    <h3 className="font-outfit font-bold text-xl mt-2" data-testid="apply-summary-name">{name}</h3>
    <p className="mt-3">{was && <s className="text-slate-400 text-lg mr-2" data-testid="apply-summary-was-price">{was}</s>}<span className="font-outfit text-4xl font-bold" data-testid="apply-summary-price">{price}</span><span className="text-slate-400 text-sm ml-2">/ {duration}</span></p>
    <ul className="mt-6 space-y-3">
      {benefits.map((b) => (
        <li key={b.text} className="flex items-center gap-3 text-sm text-slate-200">
          <span className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center"><b.icon size={15} className="text-teal-300" /></span>{b.text}
        </li>
      ))}
    </ul>
    <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between text-xs text-slate-400">
      <span className="flex items-center gap-1.5 text-teal-300"><Lock size={13} /> Secure payment via Razorpay</span>
      <span>UPI · Cards · Netbanking</span>
    </div>
  </div>
);

const METHOD = [
  [Compass, 'Diagnose', 'A readiness check and a one-to-one consultation show exactly where you stand.'],
  [ClipboardCheck, 'Plan', 'You get target roles and a personalised plan, approved by a consultant.'],
  [Hammer, 'Practise', 'Real work tasks for your role, each reviewed by a person, not just auto-graded.'],
  [Medal, 'Prove', 'A portfolio project, a stronger resume and weekly check-ins as you apply.'],
];

export const ElevateMethod = () => (
  <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="why-elevate">
    <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-teal-700">The Elevate method</p>
    <h4 className="font-outfit font-bold text-xl text-[#0b1f4d] mt-2 leading-snug">Built on real work, with a person reviewing every step.</h4>
    <ol className="mt-6 relative space-y-5">
      <span className="absolute left-[17px] top-2 bottom-2 w-px bg-slate-200" />
      {METHOD.map(([Icon, t, d]) => (
        <li key={t} className="relative flex gap-4" data-testid={`why-elevate-${t.toLowerCase()}`}>
          <span className="relative z-10 w-9 h-9 shrink-0 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center"><Icon size={16} className="text-teal-700" /></span>
          <div><p className="font-semibold text-[#0b1f4d] text-sm">{t}</p><p className="text-sm text-slate-500 leading-relaxed mt-0.5">{d}</p></div>
        </li>
      ))}
    </ol>
  </div>
);

export const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" className={`${className} fill-current`}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
);

export const HelpCard = () => (
  <div className="rounded-3xl bg-slate-50 border border-slate-200 p-6 flex items-center gap-4" data-testid="apply-help-card">
    <div className="flex-1"><p className="font-semibold text-[#0b1f4d]">Have questions first?</p><p className="text-sm text-slate-500">Talk to our team on WhatsApp.</p></div>
    <a href="https://wa.me/919311510040" target="_blank" rel="noopener noreferrer" data-testid="apply-whatsapp-btn"
      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb658] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"><WhatsAppIcon className="w-4 h-4" /> Chat</a>
  </div>
);
